(() => {

  class IngredientFocusCarousel {
    constructor(root) {
      if (root.dataset.ingredientReady === 'true') return;
      root.dataset.ingredientReady = 'true';
      this.root = root;
      this.stage = root.querySelector('[data-ingredient-stage]');
      this.slides = Array.from(root.querySelectorAll('[data-ingredient-slide]'));
      this.previousButton = root.querySelector('[data-carousel-previous]');
      this.nextButton = root.querySelector('[data-carousel-next]');
      this.activeIndex = 0;
      this.position = 0;
      this.targetPosition = 0;
      this.animationFrame = null;
      this.dragFrame = null;
      this.lastFrameTime = 0;
      this.pendingFocus = false;
      this.pointer = null;
      this.drawerPointer = null;
      this.drawerHintTimer = null;
      this.drawerHintEndTimer = null;
      this.drawerHintDialog = null;
      this.hasShownDrawerHint = false;
      this.lastTrigger = null;
      this.suppressClickUntil = 0;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.drawerMotionDuration = this.prefersReducedMotion ? 0 : 520;
      this.drawerSwipeDuration = this.prefersReducedMotion ? 0 : 360;
      this.bind();
      this.render(false);
      this.root.classList.add('is-motion-controlled');
      this.renderMotion();
    }

    bind() {
      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));

      this.slides.forEach((slide, index) => {
        const mediaButton = slide.querySelector('[data-slide-action]');

        mediaButton?.addEventListener('click', (event) => {
          if (performance.now() < this.suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          const position = slide.dataset.position;
          if (position === 'previous') {
            this.move(-1);
          } else if (position === 'next') {
            this.move(1);
          } else if (position === 'active') {
            this.openDialog(mediaButton);
          }
        });

        mediaButton?.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.move(-1, true);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.move(1, true);
          }
        });

        slide.addEventListener('click', (event) => {
          if (performance.now() < this.suppressClickUntil) {
            event.preventDefault();
            return;
          }
          if (event.target.closest('button, a')) return;
          if (slide.dataset.position === 'previous') this.move(-1);
          if (slide.dataset.position === 'next') this.move(1);
          if (slide.dataset.position === 'active') this.openDialog(mediaButton);
        });

        slide.dataset.slideIndex = String(index);
      });

      this.stage?.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.stage?.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.stage?.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.stage?.addEventListener('pointercancel', (event) => this.onPointerCancel(event));
      window.addEventListener('resize', () => this.renderMotion(false));

      this.root.querySelectorAll('[data-ingredient-dialog]').forEach((dialog) => {
        if (dialog.classList.contains('ingredient-focus__drawer')) {
          this.bindDrawerSwipe(dialog);
        }

        dialog.addEventListener('click', (event) => {
          if (event.target.closest('[data-dialog-close]')) {
            this.closeDialog(dialog);
            return;
          }
          if (event.target === dialog) this.closeDialog(dialog);
        });

        dialog.addEventListener('cancel', (event) => {
          if (!dialog.classList.contains('ingredient-focus__drawer')) return;
          event.preventDefault();
          this.closeDialog(dialog);
        });

        dialog.addEventListener('close', () => {
          this.resetDrawerSwipe(dialog);
          dialog.classList.remove('is-opening', 'is-closing');
          this.root.classList.remove('is-dialog-open');
          document.documentElement.style.overflow = '';
          const trigger = this.lastTrigger;
          if (!trigger) return;

          trigger.classList.add('is-dialog-restored');
          trigger.focus({ preventScroll: true });

          const clearRestoredFocus = () => trigger.classList.remove('is-dialog-restored');
          trigger.addEventListener('blur', clearRestoredFocus, { once: true });
          trigger.addEventListener('keydown', clearRestoredFocus, { once: true });
        });
      });
    }

    move(direction, shouldFocus = false) {
      if (this.slides.length < 2 || this.root.classList.contains('is-dialog-open')) return;
      this.targetPosition += direction;
      if (this.prefersReducedMotion) {
        this.position = this.targetPosition;
        this.renderMotion(shouldFocus);
        return;
      }
      this.startMotion(shouldFocus);
    }

    startMotion(shouldFocus = false) {
      this.pendingFocus = this.pendingFocus || shouldFocus;
      if (this.animationFrame) return;
      this.lastFrameTime = 0;

      const step = (time) => {
        if (!this.lastFrameTime) this.lastFrameTime = time;
        const elapsed = Math.min(40, Math.max(1, time - this.lastFrameTime));
        this.lastFrameTime = time;
        const distance = this.targetPosition - this.position;
        const blend = 1 - Math.exp(-elapsed / 90);

        this.position += distance * blend;

        if (Math.abs(this.targetPosition - this.position) < 0.001) {
          this.position = this.targetPosition;
          this.animationFrame = null;
          this.lastFrameTime = 0;
          this.renderMotion(this.pendingFocus);
          this.pendingFocus = false;
          return;
        }

        this.renderMotion(false);
        this.animationFrame = window.requestAnimationFrame(step);
      };

      this.animationFrame = window.requestAnimationFrame(step);
    }

    stopMotion() {
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.lastFrameTime = 0;
      this.pendingFocus = false;
    }

    onPointerDown(event) {
      if (
        this.slides.length < 2
        || this.root.classList.contains('is-dialog-open')
        || !event.isPrimary
        || (event.pointerType === 'mouse' && event.button !== 0)
      ) {
        return;
      }

      this.stopMotion();
      this.cancelDragRender();
      this.targetPosition = this.position;
      this.pointer = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: performance.now(),
        startPosition: this.position,
        lastX: event.clientX,
        lastTime: performance.now(),
        velocity: 0,
        dragX: 0,
        locked: false
      };
    }

    onPointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;

      const distanceX = event.clientX - this.pointer.startX;
      const distanceY = event.clientY - this.pointer.startY;

      if (!this.pointer.locked) {
        if (Math.abs(distanceX) < 8 && Math.abs(distanceY) < 8) return;
        if (Math.abs(distanceY) >= Math.abs(distanceX)) {
          this.pointer = null;
          return;
        }

        this.pointer.locked = true;
        this.root.classList.add('is-dragging');
        this.stage?.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();
      const travel = this.getDragTravel();
      const now = performance.now();
      const sampleTime = Math.max(1, now - this.pointer.lastTime);
      const sampleVelocity = (event.clientX - this.pointer.lastX) / sampleTime;

      this.pointer.velocity = (this.pointer.velocity * 0.55) + (sampleVelocity * 0.45);
      this.pointer.lastX = event.clientX;
      this.pointer.lastTime = now;
      this.pointer.dragX = distanceX;
      const anchorPosition = Math.round(this.pointer.startPosition);
      const rawPosition = this.pointer.startPosition - (distanceX / travel);
      this.position = this.clamp(rawPosition, anchorPosition - 1.08, anchorPosition + 1.08);
      this.targetPosition = this.position;
      this.scheduleDragRender();
    }

    onPointerUp(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      if (!this.pointer.locked) {
        this.pointer = null;
        return;
      }

      event.preventDefault();
      const travel = this.getDragTravel();
      const velocityInSlides = -this.pointer.velocity / travel;
      const projectedPosition = this.position + (velocityInSlides * 140);
      const anchorPosition = Math.round(this.pointer.startPosition);
      const projectedTarget = Math.round(projectedPosition);

      this.targetPosition = this.clamp(projectedTarget, anchorPosition - 1, anchorPosition + 1);
      this.suppressClickUntil = performance.now() + 320;
      this.cancelDragRender();
      this.releasePointer(event.pointerId);
      this.pointer = null;
      this.root.classList.remove('is-dragging');
      this.startMotion(false);
    }

    onPointerCancel(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const wasDragging = this.pointer.locked;
      const anchorPosition = Math.round(this.pointer.startPosition);
      this.releasePointer(event.pointerId);
      this.cancelDragRender();
      this.pointer = null;
      if (wasDragging) {
        this.targetPosition = this.clamp(Math.round(this.position), anchorPosition - 1, anchorPosition + 1);
        this.root.classList.remove('is-dragging');
        this.startMotion(false);
      }
    }

    releasePointer(pointerId) {
      if (!this.stage?.hasPointerCapture?.(pointerId)) return;
      this.stage.releasePointerCapture(pointerId);
    }

    scheduleDragRender() {
      if (this.dragFrame) return;
      this.dragFrame = window.requestAnimationFrame(() => {
        this.dragFrame = null;
        this.renderMotion(false);
      });
    }

    cancelDragRender() {
      if (this.dragFrame) window.cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
    }

    getMotionMetrics() {
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const viewportWidth = window.innerWidth;
      const cardWidth = this.slides[this.activeIndex]?.offsetWidth || (isDesktop ? 350 : 260);
      const sideScale = isDesktop ? 0.875 : 0.82;
      const farScale = isDesktop ? 0.68 : 0.7;
      const sideX = isDesktop
        ? cardWidth * (0.3534375 / 0.335)
        : (cardWidth * ((1 + sideScale) / 2)) + 12;
      const farX = isDesktop
        ? (viewportWidth / 2) + (cardWidth * farScale * 0.08)
        : sideX * 2;
      const sideY = isDesktop
        ? cardWidth * 0.0875
        : Math.min(viewportWidth * 0.048, 19);
      const farY = isDesktop
        ? this.clamp(viewportWidth * 0.04, 44, 54)
        : Math.min(viewportWidth * 0.096, 38);

      return { cardWidth, farScale, farX, farY, isDesktop, sideScale, sideX, sideY };
    }

    getDragTravel() {
      return this.getMotionMetrics().sideX;
    }

    lerp(start, end, progress) {
      return start + ((end - start) * progress);
    }

    clamp(value, minimum, maximum) {
      return Math.min(maximum, Math.max(minimum, value));
    }

    wrapIndex(index) {
      return ((index % this.slides.length) + this.slides.length) % this.slides.length;
    }

    relativeOffset(index) {
      const normalizedPosition = this.wrapIndex(this.position);
      let offset = index - normalizedPosition;
      const midpoint = this.slides.length / 2;
      if (offset > midpoint) offset -= this.slides.length;
      if (offset < -midpoint) offset += this.slides.length;
      return offset;
    }

    renderMotion(shouldFocus = false) {
      if (!this.slides.length) return;
      const nearestIndex = this.wrapIndex(Math.round(this.position));
      if (nearestIndex !== this.activeIndex) {
        this.activeIndex = nearestIndex;
        this.render(shouldFocus);
      }

      const { farScale, farX, farY, isDesktop, sideScale, sideX, sideY } = this.getMotionMetrics();

      this.slides.forEach((slide, index) => {
        const offset = this.relativeOffset(index);
        const absoluteOffset = Math.abs(offset);

        if (absoluteOffset > 2.05) {
          if (slide.dataset.motionVisible !== 'false') {
            slide.dataset.motionVisible = 'false';
            slide.style.opacity = '0';
            slide.style.visibility = 'hidden';
            slide.style.pointerEvents = 'none';
            slide.style.zIndex = '0';
          }
          return;
        }

        slide.dataset.motionVisible = 'true';
        const direction = Math.sign(offset);
        const nearProgress = Math.min(1, absoluteOffset);
        const farProgress = Math.min(1, Math.max(0, absoluteOffset - 1));
        const x = absoluteOffset <= 1
          ? sideX * nearProgress
          : this.lerp(sideX, farX, farProgress);
        const y = absoluteOffset <= 1
          ? this.lerp(0, sideY, nearProgress)
          : this.lerp(sideY, farY, farProgress);
        const scale = absoluteOffset <= 1
          ? this.lerp(1, sideScale, nearProgress)
          : this.lerp(sideScale, farScale, farProgress);
        const opacity = absoluteOffset <= 1
          ? 1
          : this.lerp(1, isDesktop ? 0 : 0.58, farProgress);

        slide.style.transform = `translate(calc(-50% + ${direction * x}px), ${y}px) scale(${scale})`;
        slide.style.opacity = String(opacity);
        slide.style.visibility = opacity > 0.02 ? 'visible' : 'hidden';
        slide.style.pointerEvents = absoluteOffset <= 1.05 ? 'auto' : 'none';
        slide.style.zIndex = absoluteOffset < 0.5 ? '3' : absoluteOffset < 1.5 ? '1' : '0';
      });

      if (shouldFocus) {
        this.slides[this.activeIndex]?.querySelector('[data-slide-action]')?.focus({ preventScroll: true });
      }
    }

    render(shouldFocus) {
      const previousIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length;
      const nextIndex = (this.activeIndex + 1) % this.slides.length;
      const farPreviousIndex = (this.activeIndex - 2 + this.slides.length) % this.slides.length;
      const farNextIndex = (this.activeIndex + 2) % this.slides.length;

      this.slides.forEach((slide, index) => {
        let position = 'hidden';
        if (this.slides.length > 4 && index === farPreviousIndex) position = 'far-previous';
        if (index === this.activeIndex) position = 'active';
        if (this.slides.length > 1 && index === previousIndex) position = 'previous';
        if (this.slides.length > 2 && index === nextIndex) position = 'next';
        if (this.slides.length > 4 && index === farNextIndex) position = 'far-next';

        slide.dataset.position = position;
        slide.classList.toggle('is-active', position === 'active');
        slide.classList.toggle('is-previous', position === 'previous');
        slide.classList.toggle('is-next', position === 'next');
        slide.classList.toggle('is-far-previous', position === 'far-previous');
        slide.classList.toggle('is-far-next', position === 'far-next');
        slide.classList.toggle('is-hidden', position === 'hidden');
        slide.setAttribute('aria-hidden', String(position !== 'active'));

        const mediaButton = slide.querySelector('[data-slide-action]');
        if (mediaButton) mediaButton.tabIndex = position === 'active' ? 0 : -1;
      });

      if (shouldFocus) this.slides[this.activeIndex]?.querySelector('[data-slide-action]')?.focus({ preventScroll: true });
    }

    bindDrawerSwipe(dialog) {
      const surface = dialog.querySelector('.ingredient-focus__drawer-scroll');
      if (!surface) return;

      surface.addEventListener('pointerdown', (event) => this.onDrawerPointerDown(event, dialog, surface));
      surface.addEventListener('pointermove', (event) => this.onDrawerPointerMove(event, dialog));
      surface.addEventListener('pointerup', (event) => this.onDrawerPointerUp(event, dialog));
      surface.addEventListener('pointercancel', (event) => this.onDrawerPointerCancel(event, dialog));
    }

    onDrawerPointerDown(event, dialog, surface) {
      if (this.drawerHintTimer || dialog.classList.contains('is-gesture-hinting')) {
        this.hasShownDrawerHint = true;
        this.cancelDrawerHint(dialog);
      }

      if (
        !dialog.open
        || !event.isPrimary
        || (event.pointerType === 'mouse' && event.button !== 0)
      ) {
        return;
      }

      this.drawerPointer = {
        id: event.pointerId,
        dialog,
        surface,
        startX: event.clientX,
        startY: event.clientY,
        startTime: performance.now(),
        dragX: 0,
        locked: false
      };
    }

    onDrawerPointerMove(event, dialog) {
      const pointer = this.drawerPointer;
      if (!pointer || pointer.dialog !== dialog || event.pointerId !== pointer.id) return;

      const distanceX = event.clientX - pointer.startX;
      const distanceY = event.clientY - pointer.startY;

      if (!pointer.locked) {
        if (Math.abs(distanceX) < 8 && Math.abs(distanceY) < 8) return;
        if (Math.abs(distanceY) >= Math.abs(distanceX) || distanceX < 0) {
          this.drawerPointer = null;
          return;
        }

        pointer.locked = true;
        dialog.classList.add('is-swiping');
        pointer.surface.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();
      const width = dialog.offsetWidth || 560;
      const overflow = Math.max(0, distanceX - width);
      const resistedDistance = Math.min(distanceX, width) + (overflow * 0.18);
      const progress = Math.min(1, resistedDistance / width);

      pointer.dragX = resistedDistance;
      dialog.style.setProperty('--ingredient-drawer-drag-x', `${resistedDistance}px`);
      dialog.style.setProperty('--ingredient-drawer-backdrop-opacity', String(0.62 * (1 - progress)));
    }

    onDrawerPointerUp(event, dialog) {
      const pointer = this.drawerPointer;
      if (!pointer || pointer.dialog !== dialog || event.pointerId !== pointer.id) return;
      if (!pointer.locked) {
        this.drawerPointer = null;
        return;
      }

      event.preventDefault();
      const elapsed = Math.max(1, performance.now() - pointer.startTime);
      const velocity = pointer.dragX / elapsed;
      const width = dialog.offsetWidth || 560;
      const shouldClose = pointer.dragX >= width * 0.25 || velocity >= 0.5;

      this.releaseDrawerPointer(pointer);
      this.drawerPointer = null;
      this.settleDrawerSwipe(dialog, shouldClose);
    }

    onDrawerPointerCancel(event, dialog) {
      const pointer = this.drawerPointer;
      if (!pointer || pointer.dialog !== dialog || event.pointerId !== pointer.id) return;
      const wasDragging = pointer.locked;
      this.releaseDrawerPointer(pointer);
      this.drawerPointer = null;
      if (wasDragging) this.settleDrawerSwipe(dialog, false);
    }

    releaseDrawerPointer(pointer) {
      if (!pointer.surface?.hasPointerCapture?.(pointer.id)) return;
      pointer.surface.releasePointerCapture(pointer.id);
    }

    settleDrawerSwipe(dialog, shouldClose) {
      dialog.classList.remove('is-swiping');
      dialog.classList.add('is-swipe-settling');
      const targetX = shouldClose ? dialog.offsetWidth : 0;
      const targetBackdrop = shouldClose ? 0 : 0.62;

      dialog.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        dialog.style.setProperty('--ingredient-drawer-drag-x', `${targetX}px`);
        dialog.style.setProperty('--ingredient-drawer-backdrop-opacity', String(targetBackdrop));
      });

      if (this.drawerSwipeDuration === 0) {
        if (shouldClose) dialog.close();
        else this.resetDrawerSwipe(dialog);
        return;
      }

      window.setTimeout(() => {
        if (shouldClose) {
          if (dialog.open) dialog.close();
        } else {
          this.resetDrawerSwipe(dialog);
        }
      }, this.drawerSwipeDuration);
    }

    resetDrawerSwipe(dialog) {
      this.cancelDrawerHint(dialog);
      if (this.drawerPointer?.dialog === dialog) {
        this.releaseDrawerPointer(this.drawerPointer);
        this.drawerPointer = null;
      }
      dialog.classList.remove('is-swiping', 'is-swipe-settling');
      dialog.style.removeProperty('--ingredient-drawer-drag-x');
      dialog.style.removeProperty('--ingredient-drawer-backdrop-opacity');
    }

    scheduleDrawerHint(dialog) {
      if (
        this.hasShownDrawerHint
        || this.prefersReducedMotion
        || !window.matchMedia('(max-width: 989px)').matches
      ) {
        return;
      }

      this.cancelDrawerHint(dialog);
      this.drawerHintDialog = dialog;
      this.drawerHintTimer = window.setTimeout(() => {
        this.drawerHintTimer = null;
        if (!dialog.open || this.drawerPointer || dialog.classList.contains('is-closing')) {
          this.drawerHintDialog = null;
          return;
        }

        this.hasShownDrawerHint = true;
        dialog.classList.add('is-gesture-hinting');
        this.drawerHintEndTimer = window.setTimeout(() => {
          dialog.classList.remove('is-gesture-hinting');
          this.drawerHintEndTimer = null;
          this.drawerHintDialog = null;
        }, 620);
      }, 720);
    }

    cancelDrawerHint(dialog) {
      if (this.drawerHintDialog !== dialog) {
        dialog.classList.remove('is-gesture-hinting');
        return;
      }

      if (this.drawerHintTimer) window.clearTimeout(this.drawerHintTimer);
      if (this.drawerHintEndTimer) window.clearTimeout(this.drawerHintEndTimer);
      this.drawerHintTimer = null;
      this.drawerHintEndTimer = null;
      this.drawerHintDialog = null;
      dialog.classList.remove('is-gesture-hinting');
    }

    cancelCarouselPointer() {
      if (this.pointer) this.releasePointer(this.pointer.id);
      this.pointer = null;
      this.cancelDragRender();
      this.stopMotion();
      this.targetPosition = Math.round(this.position);
      this.position = this.targetPosition;
      this.root.classList.remove('is-dragging');
      this.renderMotion(false);
    }

    openDialog(trigger) {
      if (!trigger) return;
      const dialogId = trigger.dataset.dialogId;
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (!dialog || typeof dialog.showModal !== 'function') return;
      this.cancelCarouselPointer();
      this.lastTrigger = trigger;
      this.root.classList.add('is-dialog-open');
      this.resetDrawerSwipe(dialog);
      IngredientFocusCarousel.buildDrawerContent(dialog);
      if (dialog.classList.contains('ingredient-focus__drawer')) dialog.classList.add('is-opening');
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      if (dialog.classList.contains('ingredient-focus__drawer')) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            dialog.classList.remove('is-opening');
            this.scheduleDrawerHint(dialog);
          });
        });
      }
    }

    // The homepage ships drawer contents as inert text (snippets/custom-ingredient-drawer.liquid, defer_content);
    // build them the first time the drawer opens.
    static buildDrawerContent(dialog) {
      const source = dialog.querySelector('script[data-ingredient-drawer-content]');
      if (!source) return;
      const template = document.createElement('template');
      template.innerHTML = source.textContent;
      source.replaceWith(template.content);
    }

    closeDialog(dialog) {
      if (!dialog?.open) return;
      this.resetDrawerSwipe(dialog);
      if (!dialog.classList.contains('ingredient-focus__drawer') || this.drawerMotionDuration === 0) {
        dialog.close();
        return;
      }
      if (dialog.classList.contains('is-closing')) return;
      dialog.classList.add('is-closing');
      window.setTimeout(() => {
        if (dialog.open) dialog.close();
      }, this.drawerMotionDuration);
    }
  }

  window.GarrnishIngredientFocus = IngredientFocusCarousel;

  function initIngredientFocus(scope = document) {
    scope.querySelectorAll('[data-ingredient-focus]').forEach((root) => {
      new IngredientFocusCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initIngredientFocus());
  } else {
    initIngredientFocus();
  }

  document.addEventListener('shopify:section:load', (event) => initIngredientFocus(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-ingredient-slide]');
    const root = slide?.closest('[data-ingredient-focus]');
    if (!slide || !root) return;
    const mediaButton = slide.querySelector('[data-slide-action]');
    mediaButton?.click();
  });

})();
